import { api } from '../../../services/api';
import { session } from '../../../services/session';
import { dateService } from '../../../services/date';

import { formatValue } from '../../base/common/utils';

export class RatesService {
  constructor() {
    this.Api = api;
    this.Session = session;

    this.retailerOptions = [
      { label: 'All Partners', value: 'all', adminViewLabel: '' },
      { label: 'Active Partners', value: 'active', adminViewLabel: 'Active' },
      { label: 'Inactive Partners', value: 'inactive', adminViewLabel: 'Inactive' },
      { label: 'New Partners', value: 'new,active', adminViewLabel: 'New' },
    ];

    this.strategyOptions = [
      { label: 'All Strategies', value: 'all' },
      { label: 'Active Strategies', value: 'active' },
      { label: 'Inactive Strategies', value: 'inactive' },
    ];
  }

  getDataFromActiveTab(activeTab, params) {
    return activeTab === 'partners' ? this.getRates(params) : this.getStrategies(params);
  }

  getStrategies(params) {
    const endpoint = `/api/v0/publishers/${this.Session.orgId}/strategies_v2/`;
    const validParams = this.getValidParams(params, 'strategies');
    return this.Api.get(endpoint, validParams);
  }

  getRates(params) {
    const endpoint = `/api/v0/publishers/${session.orgId}/rates_v2/`;
    const validParams = this.getValidParams(params, 'rates');
    return this.Api.get(endpoint, validParams);
  }

  getStrategyProducts(params, strategyId) {
    const endpoint = `/api/v0/publishers/${session.orgId}/strategies/
			${strategyId}/products_v2`;

    const validParams = this.getValidParams(params, 'strategy-products');
    return this.Api.get(endpoint, validParams);
  }

  updateMerchantPublisherNetwork(params) {
    const endpoint = `/api/v0/publishers/${session.orgId}/update_merchant_publisher_network/`;
    const validParams = this.getValidParams(params, 'merchant-pub-network');
    return this.Api.put(endpoint, validParams);
  }

  getValidParams(params, endpointType) {
    const baseParams = ['date_from', 'date_to', 'order_by', 'direction', 'page', 'per_page'];
    const endpointParams = {
      strategies: [
        ...baseParams,
        'merch_id',
        'strategy_id',
        'strategy_status',
        'search_query',
        'search_type',
        'campaign_label',
        'fee_types',
      ],
      'strategy-products': [...baseParams, 'product_query', 'is_in_stock'],
      rates: [
        ...baseParams,
        'merch_id',
        'pricing_models',
        'filter_by',
        'search_query',
        'search_type',
      ],
      'rates-for-merch': [...baseParams, 'pricing_models', 'filter_by'],
      evergreen: ['date_from', 'date_to', 'merch_id'],
      'merchant-pub-network': ['merch_id', 'in_network'],
    };

    return Object.keys(params).reduce(
      (acc, curr) =>
        endpointParams[endpointType].includes(curr) ? { ...acc, [curr]: params[curr] } : acc,
      {},
    );
  }

  getVisibleDataCount(dataRows, isRetailerOverview, isActiveStrategyCount, participatingCount) {
    return dataRows.filter(
      dataRow =>
        this.isVisible(dataRow, isRetailerOverview, isActiveStrategyCount) &&
        (!participatingCount ||
          (parseFloat(dataRow.clicks) && dataRow.strategy_status !== 'upcoming')),
    ).length;
  }

  getParticipatingDisplayTxt(strategies, isRetailerOverview) {
    const dataLen = this.getVisibleDataCount(strategies, isRetailerOverview, false, true);
    return dataLen
      ? `Participating in ${dataLen} ${dataLen === 1 ? 'strategy' : 'strategies'}`
      : 'Not participating in any';
  }

  isVisible(dataRow, isRetailerOverview, isActiveStrategyCount) {
    // The row is always visible if it's a partner
    if ('strategies' in dataRow) return true;

    /*
     * Admins can see:
     * 		- Admin-only strategies and public strategies that are targeted to the given pub or targeted to no pubs
     * 		- Retailers that are in-network for the given pub
     *
     * Public users (publishers) can see:
     * 		- Public strategies that are targeted to the given pub or targeted to no pubs
     * 		- Retailers that are in-network for the given pub
     */
    const adminVisible = dataRow.is_admin_only ? session.isAdminView : true;

    const isTargetedPublisher =
      dataRow.visible_to && dataRow.visible_to.length
        ? dataRow.visible_to.includes(session.orgId)
        : true;

    const isEvergreen = dataRow.strategy_name === 'Evergreen Strategy';

    // Filter strategies that are out of network from overview page
    const inNetwork = !isRetailerOverview || dataRow.merchant_in_network;

    return isEvergreen || (inNetwork && adminVisible && isTargetedPublisher);
  }

  formatStrategyRates(dataRow) {
    return {
      commission_rate:
        dataRow.pricing_model && dataRow.pricing_model === 1
          ? // Commission rate returned as percent, divide to get decimal
            `${formatValue(dataRow.commission_rate, 'currency')} per click`
          : `${formatValue(dataRow.commission_rate, 'percent', 'commission_rate')} Commission`,
      evergreen_rate:
        dataRow.pricing_model && dataRow.pricing_model === 1
          ? // Commission rate returned as percent, divide to get decimal
            `${formatValue(dataRow.evergreen_rate, 'currency')} per click`
          : `${formatValue(dataRow.evergreen_rate, 'percent', 'commission_rate')} Commission`,
      strategy_bid: dataRow.strategy_bid
        ? `${formatValue(dataRow.strategy_bid, 'currency')} per click`
        : null,
    };
  }

  getStrategyBioColumns(strategy) {
    const formattedRates = this.formatStrategyRates(strategy);
    const dateEnd = strategy.strategy_datetime_end
      ? dateService.formatDate(strategy.strategy_datetime_end, 'MM/DD/YYYY', true)
      : 'Present';
    return [
      {
        label: 'Strategy Rate',
        value: formattedRates.strategy_bid
          ? formattedRates.strategy_bid
          : formattedRates.commission_rate,
      },
      {
        label: 'Run Dates',
        value: `${dateService.formatDate(
          strategy.strategy_datetime_start,
          'MM/DD/YYYY',
          true,
        )}-${dateEnd}`,
      },
      {
        label: 'Offered By',
        value: strategy.merchant_name,
      },
    ];
  }

  getMockStrategies() {
    return [
      {
        merchant_name: 'Ulta Beauty',
        strategy_name: 'A campaign // Electronics // Sub electronics // Audio // Sub audio',
        publisher_epc: '0.15',
        commission_rate: 10.0,
        strategy_datetime_start: '2020-08-11T00:00:00Z',
        strategy_datetime_end: '2020-09-30T23:59:59Z',
        clicks: 3000,
        publisher_total_earnings: '90.00',
        merch_id: 1179,
        strategy_id: 1,
      },
    ];
  }

  getMockStrategyProducts() {
    return {
      pub_id: 1158,
      stats: [
        {
          product_name: 'SkinCeuticals Daily Moisture, 2 Fl. Oz. | Dermstore',
          image_url: 'https://images.dermstore.com/catalog/100113/300x300/1453.jpg',
          publisher_total_earnings: '10.00',
          clicks: 1500,
          publisher_epc: '0.20',
          product_price: '25.00',
          is_in_stock: true,
        },
        {
          product_name: 'JBL Flip 5 Waterproof Portable Bluetooth Speaker - Blue',
          image_url: 'https://images.crutchfieldonline.com/products/2019/24/109/h109FLIP5BL-F.jpg',
          publisher_total_earnings: '10.00',
          clicks: 15,
          publisher_epc: '0.20',
          store_page_url: 'https://bedbathandbeyond.com',
          product_price: '25.00',
          is_in_stock: true,
        },
        {
          product_name: 'Sub-Zero De-Puffing Eye Mask',
          image_url: null,
          publisher_total_earnings: '10.00',
          clicks: 15,
          publisher_epc: '0.20',
          product_price: '25.00',
          is_in_stock: false,
        },
        {
          product_name: 'Tiger Print Wrap Dress',
          image_url:
            'https://www.logolynx.com/images/logolynx/82/828135c8968a8c03ce05b80f93e37824.png',
          publisher_total_earnings: '10.00',
          clicks: 15,
          publisher_epc: '0.20',
          store_page_url: 'https://bedbathandbeyond.com',
          product_price: '25.00',
          is_in_stock: false,
        },
      ],
      page_info: {
        page: 1,
        per_page: 50,
        total_items: 4,
        total_pages: 1,
      },
    };
  }

  getSelectedFeeType(feeTypes) {
    const feeTypesSelected = feeTypes.reduce((acc, option) => {
      return option.selected ? [...acc, option.value] : acc;
    }, []);
    return feeTypesSelected.toString();
  }

  getSelectedCampaignLabels(campaignLabels) {
    const campaignLabelsSelected = campaignLabels.reduce((acc, option) => {
      return option.selected ? [...acc, option.value] : acc;
    }, []);

    return campaignLabels.length === campaignLabelsSelected.length
      ? null
      : campaignLabelsSelected.join(',');
  }

  getTabSortOptions(sortOptions, activeTab, strategyOnlySortParams, partnerOnlySortParams = []) {
    const excludedFromUpcoming = session.isAdminView
      ? []
      : ['clicks', 'publisher_total_earnings', 'publisher_epc'];
    return sortOptions.filter(
      option =>
        (activeTab.value === 'partners' || !partnerOnlySortParams.includes(option.value)) &&
        (activeTab.value !== 'upcoming strategies' ||
          !excludedFromUpcoming.includes(option.value)) &&
        (['strategies', 'upcoming strategies'].includes(activeTab.value) ||
          !strategyOnlySortParams.includes(option.value)),
    );
  }

  getDateRange(params) {
    // Return current date objects for DatePicker
    return {
      startDate: dateService.dateFromStr(params.date_from),
      endDate: dateService.dateFromStr(params.date_to),
    };
  }

  createDateRangeState(range) {
    const { startDate, endDate } = range;
    const number = -dateService.subtractDates(startDate, endDate, 'day');
    return {
      startDate,
      endDate,
      // Offset ranges for peformance comparison
      previousStartDate: dateService.offsetFromDate(startDate, number, 'day'),
      previousEndDate: dateService.offsetFromDate(endDate, number, 'day'),
    };
  }

  getDateRanges() {
    // DatePicker Dropdown Options
    const dateOffsetMap = {
      sevenDay: -7,
      thirtyDay: -30,
      ninetyDay: -90,
      thisMonth: -1,
      lastMonth: -2,
      halfYear: -6,
    };
    const presetDateRanges = dateService.getPresetDateRanges();

    return ['sevenDay', 'thirtyDay', 'ninetyDay', 'thisMonth', 'lastMonth', 'halfYear'].map(
      range => {
        const unit = ['thisMonth', 'lastMonth', 'halfYear'].includes(range) ? 'month' : 'day';
        return {
          ...presetDateRanges[range],
          name: range,
          unit,
          // Offset ranges for peformance comparison
          previousLabel: presetDateRanges[range].label
            .replace('Last', 'Previous')
            .replace('This', 'Last'),
          previousStartDate: dateService.offsetFromDate(
            presetDateRanges[range].startDate,
            dateOffsetMap[range],
            unit,
          ),
          previousEndDate: dateService.offsetFromDate(
            presetDateRanges[range].endDate,
            dateOffsetMap[range],
            unit,
          ),
        };
      },
    );
  }

  getStrategyStatus(strategy) {
    const statusMap = {
      upcoming: 'upcoming',
      expired: 'expired',
      running_and_active: 'active',
      running_and_paused: 'paused',
    };
    return statusMap[strategy.strategy_status];
  }
}

export const ratesService = new RatesService();
